// Input / NumberInput / Textarea — formularove polia
// Ustava sek. 54.3: NumPad ciarka NEPREVADZA na bodku (SK europsky styl)
// Ustava sek. 54.6: focus indicator modry
// Ustava sek. 53.5: vizualny dirty indikator (border zelenej ked je upravene)

import React from 'react'
import { COLORS, RADIUS, TYPO, SPACE, SK, FOCUS_OUTLINE, FOCUS_OFFSET } from '../theme.js'

// =============================================================
// <Input> — generic text input
// =============================================================
export function Input({
  label,
  value,
  defaultValue,
  onChange,
  placeholder,
  type = 'text',
  disabled = false,
  required = false,
  error = '',
  hint = '',
  fullWidth = false,
  autoFocus = false,
  style = {},
  inputStyle = {},
  ...rest
}) {
  const [focus, setFocus] = React.useState(false)

  // Controlled vs uncontrolled — ak nie je `value` ani `onChange`, ide o uncontrolled mode
  // (anti-pattern guard: bez onChange nemozeme controlled value menit, input by bol zamrznuty)
  const isControlled = value !== undefined && onChange !== undefined
  const inputProps = isControlled
    ? { value: value ?? '', onChange: (e) => onChange(e.target.value, e) }
    : { defaultValue: defaultValue ?? value ?? '' }

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, width: fullWidth ? '100%' : 'auto', ...style }}>
      {label && (
        <span style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, color: COLORS.text }}>
          {label}
          {required && <span style={{ color: COLORS.remove, marginLeft: 2 }}>*</span>}
        </span>
      )}
      <input
        type={type}
        {...inputProps}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        aria-invalid={!!error}
        style={{
          padding: '6px 10px',
          fontSize: TYPO.sizes.base,
          fontFamily: 'inherit',
          color: disabled ? COLORS.disabled : COLORS.text,
          background: disabled ? COLORS.lineSoft : COLORS.bg,
          border: `1px solid ${error ? COLORS.remove : focus ? COLORS.nav : COLORS.line}`,
          borderRadius: RADIUS.md,
          outline: focus ? FOCUS_OUTLINE : 'none',
          outlineOffset: FOCUS_OFFSET,
          width: fullWidth ? '100%' : 'auto',
          boxSizing: 'border-box',
          transition: 'border-color 120ms ease',
          ...inputStyle
        }}
        {...rest}
      />
      {error && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.remove }}>{error}</span>}
      {!error && hint && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{hint}</span>}
    </label>
  )
}

// =============================================================
// <NumberInput> — cislo s SK ciarkou (ustava 54.3)
// Akceptuje aj ',' aj '.' pri pisani, vracia hodnotu ako number
// (alebo null pri prazdnom inpute). Display zachova ciarku.
// =============================================================
export function NumberInput({
  label,
  value,
  onChange,
  placeholder = '0,00',
  decimals = 2,
  min,
  max,
  disabled = false,
  required = false,
  error = '',
  hint = '',
  fullWidth = false,
  align = 'right',
  style = {},
  inputStyle = {},
  ...rest
}) {
  const [focus, setFocus] = React.useState(false)
  const [raw, setRaw] = React.useState(() => SK.fmt(value, decimals))

  // Sync ked sa value zmeni z vonku a nedotykame inputu
  React.useEffect(() => {
    if (!focus) setRaw(SK.fmt(value, decimals))
  }, [value, decimals, focus])

  const handleChange = (e) => {
    const v = e.target.value
    // Povolime cifry, ciarku, bodku, minus
    const cleaned = v.replace(/[^\d,.\-]/g, '')
    setRaw(cleaned)
    // Parse na number (ciarku konvertujeme na bodku LEN pre parse, nie pre display)
    const parsed = SK.parse(cleaned)
    if (onChange) onChange(parsed, cleaned, e)
  }

  const handleBlur = () => {
    setFocus(false)
    // Pri blure normalizujeme zobrazenie
    const parsed = SK.parse(raw)
    if (parsed !== null) {
      setRaw(SK.fmt(parsed, decimals))
    }
  }

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, width: fullWidth ? '100%' : 'auto', ...style }}>
      {label && (
        <span style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, color: COLORS.text }}>
          {label}
          {required && <span style={{ color: COLORS.remove, marginLeft: 2 }}>*</span>}
        </span>
      )}
      <input
        type="text"
        inputMode="decimal"          // mobil natívna numerická klávesnica (sek. 54.7)
        value={raw}
        onChange={handleChange}
        onFocus={(e) => { setFocus(true); e.target.select() }}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        aria-invalid={!!error}
        style={{
          padding: '6px 10px',
          fontSize: TYPO.sizes.base,
          fontFamily: TYPO.mono,         // monospace pre cisla = zarovnanie
          textAlign: align,
          color: disabled ? COLORS.disabled : COLORS.text,
          background: disabled ? COLORS.lineSoft : COLORS.bg,
          border: `1px solid ${error ? COLORS.remove : focus ? COLORS.nav : COLORS.line}`,
          borderRadius: RADIUS.md,
          outline: focus ? FOCUS_OUTLINE : 'none',
          outlineOffset: FOCUS_OFFSET,
          width: fullWidth ? '100%' : 'auto',
          boxSizing: 'border-box',
          transition: 'border-color 120ms ease',
          ...inputStyle
        }}
        {...rest}
      />
      {error && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.remove }}>{error}</span>}
      {!error && hint && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{hint}</span>}
    </label>
  )
}

// =============================================================
// <Textarea>
// =============================================================
export function Textarea({
  label,
  value,
  defaultValue,
  onChange,
  placeholder,
  rows = 4,
  disabled = false,
  required = false,
  error = '',
  hint = '',
  fullWidth = true,
  style = {},
  textareaStyle = {},
  ...rest
}) {
  const [focus, setFocus] = React.useState(false)

  // Controlled vs uncontrolled (rovnaky guard ako Input — anti-pattern bez onChange)
  const isControlled = value !== undefined && onChange !== undefined
  const taProps = isControlled
    ? { value: value ?? '', onChange: (e) => onChange(e.target.value, e) }
    : { defaultValue: defaultValue ?? value ?? '' }

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, width: fullWidth ? '100%' : 'auto', ...style }}>
      {label && (
        <span style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, color: COLORS.text }}>
          {label}
          {required && <span style={{ color: COLORS.remove, marginLeft: 2 }}>*</span>}
        </span>
      )}
      <textarea
        {...taProps}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        required={required}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        aria-invalid={!!error}
        style={{
          padding: '6px 10px',
          fontSize: TYPO.sizes.base,
          fontFamily: 'inherit',
          color: disabled ? COLORS.disabled : COLORS.text,
          background: disabled ? COLORS.lineSoft : COLORS.bg,
          border: `1px solid ${error ? COLORS.remove : focus ? COLORS.nav : COLORS.line}`,
          borderRadius: RADIUS.md,
          outline: focus ? FOCUS_OUTLINE : 'none',
          outlineOffset: FOCUS_OFFSET,
          width: fullWidth ? '100%' : 'auto',
          resize: 'vertical',
          boxSizing: 'border-box',
          lineHeight: TYPO.line.base,
          transition: 'border-color 120ms ease',
          ...textareaStyle
        }}
        {...rest}
      />
      {error && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.remove }}>{error}</span>}
      {!error && hint && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{hint}</span>}
    </label>
  )
}

export default Input
